import type { ReactNode } from "react"
import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Placeholder from "@tiptap/extension-placeholder"
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Quote,
  Minus,
  type LucideIcon,
} from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * The note editor, redrawn to canvas 18b.
 *
 * It used to be a bordered box with a filled toolbar strip on top — a card
 * inside whatever screen it sat in, which is the exact pattern §18 removes.
 * Here the field is flush with the page and the toolbar sits under it on a
 * hairline, as a row of quiet icons.
 *
 * Same name and props as before, so the job notes, the candidate comment
 * viewer and the community person notes all pick this up.
 */

interface RichTextEditorProps {
  content: string
  onChange: (content: string) => void
  placeholder?: string
  className?: string
  /**
   * Sits at the end of the toolbar row. 18b puts the state of the note there —
   * "first note on this job" — rather than in a line of its own.
   */
  trailing?: ReactNode
}

const TOOLS: { icon: LucideIcon; label: string; is?: string; run: (editor: Editor) => void }[] = [
  { icon: Bold, label: "Bold", is: "bold", run: (e) => e.chain().focus().toggleBold().run() },
  { icon: Italic, label: "Italic", is: "italic", run: (e) => e.chain().focus().toggleItalic().run() },
  {
    icon: Heading2,
    label: "Heading",
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    icon: List,
    label: "Bullet list",
    is: "bulletList",
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    icon: ListOrdered,
    label: "Numbered list",
    is: "orderedList",
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    icon: Quote,
    label: "Quote",
    is: "blockquote",
    run: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  { icon: Minus, label: "Divider", run: (e) => e.chain().focus().setHorizontalRule().run() },
]

export function RichTextEditor({
  content,
  onChange,
  placeholder,
  className,
  trailing,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Placeholder.configure({ placeholder: placeholder || "Start typing…" }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none min-h-[140px] py-1 text-[14px] leading-[1.75] focus:outline-none [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1 [&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:h-0 [&_p.is-editor-empty:first-child::before]:text-muted-foreground [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
      },
    },
  })

  if (!editor) return null

  return (
    <div className={cn("@container flex min-h-0 flex-col", className)}>
      {/*
        Above the field, not below it. §18 draws the toolbar underneath, but a
        toolbar is where people reach before they type, and every editor they
        use puts it at the top — the artboard is a picture, and this is the part
        of it that fights what the control is for.
      */}
      <div className="flex flex-none items-center gap-0.5 border-b pb-2">
        {TOOLS.map(({ icon: Icon, label, is, run }) => (
          <button
            key={label}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={is ? editor.isActive(is) : undefined}
            /* Keeps the caret where it is: the button must not take focus. */
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => run(editor)}
            className={cn(
              "flex h-8 w-8 flex-none items-center justify-center rounded-md transition-colors",
              is && editor.isActive(is)
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
        {/* Seven controls already fill a phone; anything trailing them waits
            for the room to say it. */}
        {trailing ? (
          <span className="ml-auto hidden items-center gap-4 pl-3 @sm:flex">{trailing}</span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
